import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Mail,
  Calendar,
  Scale,
  AlertTriangle,
  Shield,
  Gavel,
  BookOpen,
  Ban,
  CreditCard,
  ArrowLeft,
  CheckCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SEO } from "../components/SEO";

export function TermsOfServicePage() {
  const navigate = useNavigate();
  const lastUpdated = "January 26, 2026";
  const effectiveDate = "January 26, 2026";

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO />
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
        <div className="bg-slate-900 text-white py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-4"
            >
              <div className="w-16 h-16 rounded-xl bg-teal-500/20 flex items-center justify-center">
                <FileText className="w-8 h-8 text-teal-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Terms of Service</h1>
                <p className="text-slate-400 mt-1">Builder Buddy Canada</p>
              </div>
            </motion.div>
            <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-400">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Last Updated: {lastUpdated}
              </span>
              <span className="flex items-center gap-2">
                <Scale className="w-4 h-4" />
                Effective Date: {effectiveDate}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="prose prose-slate max-w-none"
          >
            {/* Important Notice */}
            <section className="mb-12">
              <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-red-900 mb-2">
                      IMPORTANT: PLEASE READ CAREFULLY
                    </h3>
                    <p className="text-red-800 text-sm leading-relaxed mb-3">
                      These Terms of Service ("Terms") constitute a legally
                      binding agreement between you and Builder Buddy Canada. By
                      accessing or using our Service, you agree to be bound by
                      these Terms. If you do not agree to these Terms, you must
                      not access or use our Service.
                    </p>
                    <p className="text-red-800 text-sm leading-relaxed font-semibold">
                      THESE TERMS CONTAIN IMPORTANT DISCLAIMERS OF WARRANTIES,
                      LIMITATIONS OF LIABILITY, AND INDEMNIFICATION PROVISIONS.
                      PLEASE READ THEM CAREFULLY.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Agreement to Terms */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Scale className="w-6 h-6 text-teal-600" />
                1. Agreement to Terms
              </h2>
              <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
                <p className="text-slate-700 leading-relaxed mb-4">
                  These Terms of Service ("Terms") govern your access to and use
                  of Builder Buddy Canada's website, applications, and services
                  (collectively, the "Service"), operated by Builder Buddy
                  Canada, a division of Tam-Crete Engineering ("Builder Buddy,"
                  "we," "us," or "our").
                </p>
                <p className="text-slate-700 leading-relaxed mb-4">
                  By accessing, browsing, or using the Service, or by clicking
                  "I agree" or similar button, you acknowledge that you have
                  read, understood, and agree to be bound by these Terms and our
                  Privacy Policy, which is incorporated herein by reference.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  If you are using the Service on behalf of a company,
                  organization, or other entity, you represent and warrant that
                  you have authority to bind such entity to these Terms, and
                  "you" shall refer to such entity.
                </p>
              </div>
            </section>

            {/* Eligibility */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-teal-600" />
                2. Eligibility
              </h2>
              <p className="text-slate-700 mb-4">
                To use our Service, you must:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>
                  Be at least 18 years of age or the age of legal majority in
                  your jurisdiction;
                </li>
                <li>
                  Have the legal capacity to enter into a binding agreement;
                </li>
                <li>
                  Not be prohibited from using the Service under applicable
                  laws;
                </li>
                <li>
                  Provide accurate and complete information when creating an
                  account;
                </li>
                <li>
                  If using the Service for professional purposes, hold
                  appropriate professional credentials and licenses as required
                  by applicable law.
                </li>
              </ul>
            </section>

            {/* Description of Service */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <BookOpen className="w-6 h-6 text-teal-600" />
                3. Description of Service
              </h2>
              <p className="text-slate-700 mb-4">
                Builder Buddy provides an AI-powered platform designed to assist
                users with building code research, permit applications,
                construction planning, and related activities. The Service
                includes, but is not limited to:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>
                  <strong>AI Chat Assistant:</strong> An artificial
                  intelligence-based tool that provides information about
                  building codes, regulations, and construction standards.
                </li>
                <li>
                  <strong>Code Library:</strong> A searchable database of
                  building codes, bylaws, and regulatory documents.
                </li>
                <li>
                  <strong>Permit Tracking:</strong> Tools for tracking permit
                  applications and requirements.
                </li>
                <li>
                  <strong>SDAB Expert:</strong> Analysis tools for development
                  permit and appeal risk assessment.
                </li>
                <li>
                  <strong>Quantity Survey:</strong> Estimation tools for
                  construction materials and costs.
                </li>
                <li>
                  <strong>Feasibility Studies:</strong> Project viability
                  assessment tools.
                </li>
                <li>
                  <strong>Address Autocomplete:</strong> Property lookup and
                  zoning information services.
                </li>
              </ul>
            </section>

            {/* Critical Disclaimers */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-teal-600" />
                4. Critical Disclaimers
              </h2>

              <div className="bg-amber-50 rounded-lg p-6 border border-amber-200 mb-6">
                <h3 className="font-bold text-amber-900 mb-3">
                  4.1 NOT PROFESSIONAL ADVICE
                </h3>
                <p className="text-amber-800 text-sm leading-relaxed mb-3">
                  THE SERVICE IS PROVIDED FOR INFORMATIONAL AND EDUCATIONAL
                  PURPOSES ONLY. THE INFORMATION, CONTENT, AND MATERIALS
                  PROVIDED THROUGH THE SERVICE, INCLUDING AI-GENERATED
                  RESPONSES, DO NOT CONSTITUTE AND SHALL NOT BE CONSTRUED AS:
                </p>
                <ul className="list-disc pl-6 text-amber-800 text-sm space-y-1">
                  <li>Professional engineering advice or opinions;</li>
                  <li>Architectural advice or design recommendations;</li>
                  <li>Legal advice or legal opinions;</li>
                  <li>Certified building code interpretations;</li>
                  <li>Official permit or regulatory guidance;</li>
                  <li>Professional quantity surveying or cost estimation;</li>
                  <li>Financial or investment advice.</li>
                </ul>
              </div>

              <div className="bg-amber-50 rounded-lg p-6 border border-amber-200 mb-6">
                <h3 className="font-bold text-amber-900 mb-3">
                  4.2 AI LIMITATIONS
                </h3>
                <p className="text-amber-800 text-sm leading-relaxed">
                  Our AI systems, while trained on extensive building code data,
                  may produce inaccurate, incomplete, outdated, or misleading
                  information. AI-generated content should NEVER be relied upon
                  as the sole basis for construction decisions, permit
                  applications, building designs, or any other professional or
                  regulatory matters. ALL AI-generated information must be
                  independently verified by qualified professionals before use.
                </p>
              </div>

              <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
                <h3 className="font-bold text-amber-900 mb-3">
                  4.3 PROFESSIONAL CONSULTATION REQUIRED
                </h3>
                <p className="text-amber-800 text-sm leading-relaxed">
                  YOU MUST CONSULT WITH QUALIFIED, LICENSED PROFESSIONALS
                  (including but not limited to professional engineers,
                  architects, lawyers, certified building officials, and other
                  relevant experts) BEFORE MAKING ANY DECISIONS BASED ON
                  INFORMATION OBTAINED THROUGH THE SERVICE. Builder Buddy is a
                  research and reference tool only and is not a substitute for
                  professional judgment.
                </p>
              </div>
            </section>

            {/* User Accounts */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-teal-600" />
                5. User Accounts and Security
              </h2>
              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                5.1 Account Registration
              </h3>
              <p className="text-slate-700 mb-4">
                Some features of the Service require you to create an account.
                When registering, you agree to provide accurate, current, and
                complete information, and to update such information to keep it
                accurate, current, and complete.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                5.2 Account Security
              </h3>
              <p className="text-slate-700 mb-4">
                You are responsible for safeguarding your account credentials
                and for all activities that occur under your account. You must
                notify us immediately of any unauthorized use of your account or
                any other breach of security. We will not be liable for any loss
                or damage arising from your failure to maintain the security of
                your account.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                5.3 Account Termination
              </h3>
              <p className="text-slate-700">
                We reserve the right to suspend or terminate your account at any
                time, for any reason, including but not limited to violation of
                these Terms, without prior notice or liability.
              </p>
            </section>

            {/* Acceptable Use */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Ban className="w-6 h-6 text-teal-600" />
                6. Acceptable Use Policy
              </h2>
              <p className="text-slate-700 mb-4">
                You agree not to use the Service to:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>
                  Violate any applicable law, regulation, or third-party rights;
                </li>
                <li>Submit false, misleading, or fraudulent information;</li>
                <li>
                  Impersonate any person or entity or misrepresent your
                  affiliation;
                </li>
                <li>
                  Interfere with or disrupt the Service or servers or networks
                  connected to the Service;
                </li>
                <li>
                  Attempt to gain unauthorized access to any part of the Service
                  or any systems or networks;
                </li>
                <li>
                  Use any robot, spider, scraper, or other automated means to
                  access the Service without our express written permission;
                </li>
                <li>
                  Reverse engineer, decompile, or disassemble any aspect of the
                  Service;
                </li>
                <li>
                  Remove, alter, or obscure any proprietary notices on the
                  Service;
                </li>
                <li>
                  Use the Service for any unlawful, harmful, or fraudulent
                  purpose;
                </li>
                <li>Transmit any viruses, malware, or other harmful code;</li>
                <li>Harass, abuse, or harm another person or entity;</li>
                <li>
                  Collect or store personal information about other users
                  without their consent;
                </li>
                <li>
                  Use the Service for competitive intelligence or to develop a
                  competing product;
                </li>
                <li>
                  Resell, sublicense, or redistribute the Service without our
                  express written permission.
                </li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Gavel className="w-6 h-6 text-teal-600" />
                7. Intellectual Property Rights
              </h2>
              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                7.1 Our Intellectual Property
              </h3>
              <p className="text-slate-700 mb-4">
                The Service, including its entire contents, features, and
                functionality (including but not limited to all information,
                software, text, displays, images, video, audio, design,
                selection, and arrangement), is owned by Builder Buddy, its
                licensors, or other providers and is protected by Canadian and
                international copyright, trademark, patent, trade secret, and
                other intellectual property laws.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                7.2 Third-Party Content
              </h3>
              <p className="text-slate-700 mb-4">
                The Service may contain building codes, regulations, and other
                content owned by government entities and third parties. Such
                content is used under applicable licenses or fair use
                provisions. We do not claim ownership of government-published
                building codes and regulations.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                7.3 Limited License
              </h3>
              <p className="text-slate-700 mb-4">
                Subject to these Terms, we grant you a limited, non-exclusive,
                non-transferable, revocable license to access and use the
                Service for your personal or internal business purposes. This
                license does not include the right to: (a) modify or copy the
                Service; (b) use the Service for commercial purposes not
                contemplated by these Terms; (c) transfer, assign, or sublicense
                your rights.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                7.4 Your Content
              </h3>
              <p className="text-slate-700">
                You retain ownership of any content you submit through the
                Service. By submitting content, you grant us a worldwide,
                non-exclusive, royalty-free, sublicensable license to use,
                reproduce, modify, adapt, publish, translate, and distribute
                your content in connection with operating and improving the
                Service.
              </p>
            </section>

            {/* Payment Terms */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <CreditCard className="w-6 h-6 text-teal-600" />
                8. Payment Terms
              </h2>
              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                8.1 Fees
              </h3>
              <p className="text-slate-700 mb-4">
                Certain features of the Service may require payment. You agree
                to pay all applicable fees as described on our website. All fees
                are quoted in Canadian dollars unless otherwise specified.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                8.2 Billing
              </h3>
              <p className="text-slate-700 mb-4">
                By providing payment information, you authorize us to charge the
                applicable fees to your payment method. Subscription fees are
                billed in advance on a recurring basis. We may change our fees
                at any time with 30 days' notice.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                8.3 Refunds
              </h3>
              <p className="text-slate-700 mb-4">
                Except as required by law, all fees are non-refundable. If you
                cancel a subscription, you will continue to have access until
                the end of your current billing period.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                8.4 Taxes
              </h3>
              <p className="text-slate-700">
                You are responsible for all applicable taxes, including GST/HST
                where applicable. We will collect taxes where required by law.
              </p>
            </section>

            {/* Disclaimer of Warranties */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-teal-600" />
                9. Disclaimer of Warranties
              </h2>
              <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                <p className="text-red-800 font-semibold mb-4">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                  WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING
                  BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
                  FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE
                  OF PERFORMANCE.
                </p>
                <p className="text-red-800 mb-4">
                  BUILDER BUDDY, ITS AFFILIATES, LICENSORS, AND SERVICE
                  PROVIDERS MAKE NO WARRANTY THAT:
                </p>
                <ul className="list-disc pl-6 text-red-800 space-y-2">
                  <li>
                    The Service will meet your requirements or expectations;
                  </li>
                  <li>
                    The Service will be uninterrupted, timely, secure, or
                    error-free;
                  </li>
                  <li>
                    The information, content, or results obtained through the
                    Service will be accurate, reliable, complete, or current;
                  </li>
                  <li>
                    Any errors or defects in the Service will be corrected;
                  </li>
                  <li>
                    The Service will be free from viruses or other harmful
                    components;
                  </li>
                  <li>
                    AI-generated content will be accurate, appropriate, or
                    suitable for any purpose;
                  </li>
                  <li>
                    Building code information will reflect current applicable
                    codes or regulations.
                  </li>
                </ul>
                <p className="text-red-800 mt-4">
                  NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED
                  FROM BUILDER BUDDY OR THROUGH THE SERVICE WILL CREATE ANY
                  WARRANTY NOT EXPRESSLY STATED IN THESE TERMS.
                </p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Scale className="w-6 h-6 text-teal-600" />
                10. Limitation of Liability
              </h2>
              <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                <p className="text-red-800 font-semibold mb-4">
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
                </p>
                <p className="text-red-800 mb-4">
                  (a) IN NO EVENT SHALL BUILDER BUDDY, ITS DIRECTORS, OFFICERS,
                  EMPLOYEES, AGENTS, AFFILIATES, LICENSORS, OR SERVICE PROVIDERS
                  BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                  CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT
                  NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE,
                  DATA, OR OTHER INTANGIBLE LOSSES, REGARDLESS OF WHETHER SUCH
                  DAMAGES WERE FORESEEABLE AND WHETHER OR NOT BUILDER BUDDY WAS
                  ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>
                <p className="text-red-800 mb-4">
                  (b) WITHOUT LIMITING THE FOREGOING, BUILDER BUDDY SHALL NOT BE
                  LIABLE FOR ANY DAMAGES ARISING FROM:
                </p>
                <ul className="list-disc pl-6 text-red-800 space-y-1 mb-4">
                  <li>
                    Your reliance on any information or content obtained through
                    the Service;
                  </li>
                  <li>
                    Any construction defects, code violations, permit denials,
                    or regulatory penalties;
                  </li>
                  <li>
                    Any personal injury, property damage, or economic loss
                    resulting from construction activities;
                  </li>
                  <li>
                    Any errors, omissions, or inaccuracies in AI-generated
                    content;
                  </li>
                  <li>Unauthorized access to or alteration of your data;</li>
                  <li>Any third-party conduct or content.</li>
                </ul>
                <p className="text-red-800 font-semibold">
                  (c) IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL
                  CLAIMS ARISING FROM OR RELATED TO THE SERVICE EXCEED THE
                  GREATER OF: (i) THE AMOUNT YOU PAID TO US FOR THE SERVICE IN
                  THE TWELVE (12) MONTHS PRECEDING THE CLAIM; OR (ii) ONE
                  HUNDRED CANADIAN DOLLARS (CAD $100).
                </p>
              </div>
            </section>

            {/* Indemnification */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-teal-600" />
                11. Indemnification
              </h2>
              <p className="text-slate-700 mb-4">
                You agree to defend, indemnify, and hold harmless Builder Buddy,
                its affiliates, licensors, and service providers, and their
                respective officers, directors, employees, contractors, agents,
                licensors, suppliers, successors, and assigns from and against
                any claims, liabilities, damages, judgments, awards, losses,
                costs, expenses, or fees (including reasonable attorneys' fees)
                arising out of or relating to:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Your violation of these Terms;</li>
                <li>Your use of the Service;</li>
                <li>
                  Your violation of any third-party rights, including
                  intellectual property rights;
                </li>
                <li>
                  Any construction, development, or building activities
                  undertaken based on information from the Service;
                </li>
                <li>Any content you submit through the Service;</li>
                <li>Your violation of any applicable law or regulation.</li>
              </ul>
            </section>

            {/* Governing Law */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Gavel className="w-6 h-6 text-teal-600" />
                12. Governing Law and Dispute Resolution
              </h2>
              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                12.1 Governing Law
              </h3>
              <p className="text-slate-700 mb-4">
                These Terms and any dispute arising out of or related to these
                Terms or the Service shall be governed by and construed in
                accordance with the laws of the Province of Alberta and the
                federal laws of Canada applicable therein, without regard to
                conflict of law principles.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                12.2 Jurisdiction
              </h3>
              <p className="text-slate-700 mb-4">
                You agree to submit to the exclusive jurisdiction of the courts
                located in Calgary, Alberta, Canada for any dispute arising out
                of or related to these Terms or the Service.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">
                12.3 Class Action Waiver
              </h3>
              <p className="text-slate-700">
                TO THE EXTENT PERMITTED BY LAW, YOU AND BUILDER BUDDY AGREE THAT
                EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS
                INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN
                ANY PURPORTED CLASS, COLLECTIVE, OR REPRESENTATIVE PROCEEDING.
              </p>
            </section>

            {/* Modifications */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Calendar className="w-6 h-6 text-teal-600" />
                13. Modifications to Terms
              </h2>
              <p className="text-slate-700 mb-4">
                We reserve the right to modify these Terms at any time. We will
                notify you of material changes by posting the updated Terms on
                our website with a new "Last Updated" date. Your continued use
                of the Service after the effective date of any modifications
                constitutes your acceptance of the modified Terms.
              </p>
              <p className="text-slate-700">
                If you do not agree to the modified Terms, you must stop using
                the Service and may request deletion of your account.
              </p>
            </section>

            {/* Termination */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Ban className="w-6 h-6 text-teal-600" />
                14. Termination
              </h2>
              <p className="text-slate-700 mb-4">
                We may terminate or suspend your access to the Service
                immediately, without prior notice or liability, for any reason,
                including breach of these Terms. Upon termination:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Your right to use the Service will immediately cease;</li>
                <li>We may delete your account and all associated data;</li>
                <li>
                  Provisions of these Terms that by their nature should survive
                  termination shall survive, including ownership provisions,
                  warranty disclaimers, indemnification, and limitations of
                  liability.
                </li>
              </ul>
            </section>

            {/* General Provisions */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <BookOpen className="w-6 h-6 text-teal-600" />
                15. General Provisions
              </h2>
              <div className="space-y-4 text-slate-700">
                <p>
                  <strong>15.1 Entire Agreement:</strong> These Terms, together
                  with our Privacy Policy, constitute the entire agreement
                  between you and Builder Buddy regarding the Service and
                  supersede all prior agreements and understandings.
                </p>
                <p>
                  <strong>15.2 Severability:</strong> If any provision of these
                  Terms is found to be invalid or unenforceable, the remaining
                  provisions will remain in full force and effect.
                </p>
                <p>
                  <strong>15.3 Waiver:</strong> No waiver of any term or
                  condition of these Terms shall be deemed a further or
                  continuing waiver of such term or any other term.
                </p>
                <p>
                  <strong>15.4 Assignment:</strong> You may not assign or
                  transfer these Terms or your rights under these Terms without
                  our prior written consent. We may assign these Terms without
                  restriction.
                </p>
                <p>
                  <strong>15.5 Force Majeure:</strong> We shall not be liable
                  for any failure or delay in performance due to circumstances
                  beyond our reasonable control, including natural disasters,
                  acts of government, pandemics, or service provider failures.
                </p>
                <p>
                  <strong>15.6 No Third-Party Beneficiaries:</strong> These
                  Terms do not confer any rights on any third party.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-teal-600" />
                16. Contact Information
              </h2>
              <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
                <p className="text-slate-700 mb-4">
                  If you have any questions about these Terms of Service, please
                  contact us:
                </p>
                <div className="space-y-3 text-slate-700">
                  <p>
                    <strong>Builder Buddy Canada</strong>
                    <br />A division of Tam-Crete Engineering
                  </p>
                  <p>
                    <strong>Address:</strong> Calgary, Alberta, Canada
                  </p>
                  <p>
                    <strong>Phone:</strong> (403) 333-5025
                  </p>
                  <p>
                    <strong>Email:</strong> legal@builderbuddy.ca
                  </p>
                  <p>
                    <strong>Website:</strong> https://builderbuddy.ca
                  </p>
                </div>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="mb-12">
              <div className="bg-teal-50 rounded-lg p-6 border border-teal-200">
                <h3 className="font-bold text-teal-900 mb-3">Acknowledgment</h3>
                <p className="text-teal-800 text-sm leading-relaxed">
                  BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE
                  TERMS OF SERVICE, UNDERSTAND THEM, AND AGREE TO BE BOUND BY
                  THEIR TERMS AND CONDITIONS. YOU FURTHER ACKNOWLEDGE THAT THESE
                  TERMS CONSTITUTE THE COMPLETE AND EXCLUSIVE STATEMENT OF THE
                  AGREEMENT BETWEEN YOU AND BUILDER BUDDY, WHICH SUPERSEDES ANY
                  PRIOR PROPOSALS OR AGREEMENTS, ORAL OR WRITTEN, AND ANY OTHER
                  COMMUNICATIONS RELATING TO THE SUBJECT MATTER OF THESE TERMS.
                </p>
              </div>
            </section>
          </motion.div>
        </div>

        {/* Footer */}
        <div className="bg-slate-100 border-t border-slate-200 py-6 sm:py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <p className="text-slate-600 text-sm">
              &copy; {new Date().getFullYear()} Builder Buddy Canada. All rights
              reserved.
            </p>
            <div className="mt-4 flex justify-center gap-4 sm:gap-6 text-sm">
              <button
                onClick={() => navigate("/privacy")}
                className="text-teal-600 hover:text-teal-700"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => navigate("/")}
                className="text-teal-600 hover:text-teal-700"
              >
                Home
              </button>
              <button
                onClick={() => navigate("/chat")}
                className="text-teal-600 hover:text-teal-700"
              >
                AI Chat
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
